/**
 * What happened to an invitation, under the form that sent it (redesign §41.5,
 * item 65): a note (§10.6) with its title 600 and one sentence, « Dismiss »
 * (discreet S) at its right. Sent, it is the ok note and that sentence is all
 * (« Invitation sent to new.hire@acme-platform.test. ») — no link. The mail did
 * not go: amber, and the link waits in a copyable read-only field (§3, white in
 * the note, mono 12, truncated) whose « Copy » is its label and says « Copied »
 * for 1.6 s — that link is then the invitation.
 */
import { useEffect, useRef, useState } from 'react';
import type React from 'react';
import { color, font, radius } from '../theme/tokens';
import { useMotionMount } from '../lib/presence';

export function InviteResult({
  email,
  url,
  delivery,
  onDismiss
}: {
  email: string;
  url: string;
  delivery: { sent: true } | { sent: false; reason: string };
  onDismiss: () => void;
}): React.JSX.Element {
  const reveal = useMotionMount<HTMLDivElement>({ o: 0, y: 4 }, { d: 1, r: 0.55 });
  const [copied, setCopied] = useState(false);
  const timer = useRef(0);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const copy = (): void => {
    void navigator.clipboard?.writeText(url).then(() => {
      setCopied(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 1600);
    });
  };

  const [background, ink] = delivery.sent ? [color.okBg, color.okText] : [color.warnBg, color.warnText];
  return (
    <div
      ref={reveal}
      role="status"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 14,
        maxWidth: 760,
        padding: '12px 14px',
        borderRadius: radius.md,
        background,
        color: ink,
        fontSize: 13,
        lineHeight: 1.5
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        {delivery.sent ? (
          <div style={{ fontWeight: 600 }}>{`Invitation sent to ${email}.`}</div>
        ) : (
          <>
            <div style={{ fontWeight: 600 }}>{`${email} was invited — the mail did not go out`}</div>
            <div>{`${delivery.reason} The invitation exists all the same — pass this link on and it works the same way.`}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
              <input
                readOnly
                value={url}
                aria-label="The invitation's link"
                onFocus={(event) => event.currentTarget.select()}
                style={{
                  flex: 1,
                  minWidth: 0,
                  height: 32,
                  padding: '0 10px',
                  border: `1px solid ${color.line}`,
                  borderRadius: 8,
                  background: color.surface,
                  fontFamily: font.mono,
                  fontSize: 12,
                  color: color.ink,
                  textOverflow: 'ellipsis'
                }}
              />
              <button
                type="button"
                onClick={copy}
                className="hdr-btn"
                data-press=""
                style={{
                  flex: 'none',
                  height: 28,
                  padding: '0 11px',
                  border: `1px solid ${color.line}`,
                  borderRadius: radius.chip,
                  background: color.surface,
                  fontFamily: 'inherit',
                  fontSize: 12.5,
                  color: color.inkBody,
                  cursor: 'pointer'
                }}
              >
                <span aria-live="polite">{copied ? 'Copied' : 'Copy'}</span>
              </button>
            </div>
          </>
        )}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        style={{
          flex: 'none',
          background: 'none',
          border: 0,
          padding: 0,
          fontFamily: 'inherit',
          fontSize: 13,
          color: ink,
          cursor: 'pointer'
        }}
      >
        Dismiss
      </button>
    </div>
  );
}
